// ============================================
// Auth Store (Zustand) - Direct Login Mode
// ============================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Auth endpoints use Worker directly to set cookies on worker domain
// Then proxy passes cookies to worker for subsequent requests
const AUTH_BASE = 'https://crm-sales-os-api.bawaya-2019.workers.dev';
const API_BASE = '';

interface User {
  id: string;
  phone: string;
  name: string;
  role: 'owner' | 'admin' | 'agent';
}

interface AuthState {
  user: User | null;
  sessionToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (user: User, sessionToken: string) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  directLogin: () => Promise<{ ok: boolean; error?: string }>;
  startLogin: (phone: string) => Promise<{ ok: boolean; bypass?: boolean; error?: string }>;
  verifyOtp: (phone: string, code: string) => Promise<{ ok: boolean; error?: string }>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      sessionToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: (user, sessionToken) => set({
        user,
        sessionToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      }),

      logout: () => set({
        user: null,
        sessionToken: null,
        isAuthenticated: false,
      }),

      updateUser: (updates) => set((state) => ({
        user: state.user ? { ...state.user, ...updates } : null,
      })),

      clearError: () => set({ error: null }),

      // Direct login without OTP - for development/demo mode
      directLogin: async () => {
        set({ isLoading: true, error: null });
        try {
          // Create a demo user directly without API call
          const user: User = {
            id: 'owner-1',
            phone: '+972500000000',
            name: 'مدير النظام',
            role: 'owner',
          };
          
          // Generate a simple session token
          const sessionToken = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          
          set({ 
            user, 
            sessionToken, 
            isAuthenticated: true, 
            isLoading: false 
          });
          
          return { ok: true };
        } catch (err) {
          const error = err instanceof Error ? err.message : 'Login failed';
          set({ isLoading: false, error });
          return { ok: false, error };
        }
      },

      startLogin: async (phone: string) => {
        set({ isLoading: true, error: null });
        try {
          const res = await fetch(`${AUTH_BASE}/api/auth/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ phone }),
          });
          const data = await res.json();
          
          if (!res.ok) {
            set({ isLoading: false, error: data.error || 'Failed to start login' });
            return { ok: false, error: data.error };
          }
          
          // Bypass mode - already authenticated
          if (data.bypass && data.sessionToken) {
            const user: User = {
              id: data.userId || 'user-1',
              phone: phone,
              name: data.name || 'User',
              role: data.role || 'owner',
            };
            set({ 
              user, 
              sessionToken: data.sessionToken, 
              isAuthenticated: true, 
              isLoading: false 
            });
            return { ok: true, bypass: true };
          }
          
          set({ isLoading: false });
          return { ok: true };
        } catch (err) {
          const error = err instanceof Error ? err.message : 'Network error';
          set({ isLoading: false, error });
          return { ok: false, error };
        }
      },

      verifyOtp: async (phone: string, code: string) => {
        set({ isLoading: true, error: null });
        try {
          const res = await fetch(`${AUTH_BASE}/api/auth/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ phone, code }),
          });
          const data = await res.json();
          
          if (!res.ok) {
            set({ isLoading: false, error: data.error || 'Invalid code' });
            return { ok: false, error: data.error };
          }
          
          const user: User = {
            id: data.userId || 'user-1',
            phone: phone,
            name: data.name || 'User',
            role: data.role || 'owner',
          };
          
          set({ 
            user, 
            sessionToken: data.sessionToken, 
            isAuthenticated: true, 
            isLoading: false 
          });
          return { ok: true };
        } catch (err) {
          const error = err instanceof Error ? err.message : 'Network error';
          set({ isLoading: false, error });
          return { ok: false, error };
        }
      },
    }),
    {
      name: 'crm-auth',
      partialize: (state) => ({
        user: state.user,
        sessionToken: state.sessionToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
